/* Movatic WMS — Frontend | CesarFilho 932048016 */
// CopackOrderNew.tsx
// -----------------------------------------------------------------------------
// Tela para criar uma nova Copack Order equivalente ao Admin do Django.
// - Não pede "code" (é gerado no backend: CPK-001/AAAA).
// - Campos: Site (opcional), Warehouse (obrigatório), Produto final (obrigatório), Qty (>=1).
// - Usa o cliente central api.ts e trata 401/403/erros de validação.
// - Carrega Sites e Warehouses do core_api, filtrando por site quando possível.
// - Ajuste de contraste: fundo preto e letras brancas no modo escuro, alinhado às
//   telas "Usuários" e "Operadores".
// -----------------------------------------------------------------------------

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";

// Tipagens simples para o que precisamos
type Site = { id: number; code: string; name?: string };
type Warehouse = { id: number; code: string; name?: string; site?: number };

type Option = { value: number; label: string };

function toOptions<T extends { id: number; code: string; name?: string }>(rows: T[]): Option[] {
    return (rows || []).map((r) => ({ value: r.id, label: r.code + (r.name ? ` — ${r.name}` : "") }));
}

async function fetchList<T>(url: string): Promise<T[]> {
    // Normaliza base /api (api.ts já resolve /api duplicado)
    const { data } = await api.get(url);
    // Aceita array puro ou shape paginado {results: []}
    return Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
}

export default function CopackOrderNew() {
    const nav = useNavigate();

    // Form state
    const [siteId, setSiteId] = useState<number | "">("");
    const [warehouseId, setWarehouseId] = useState<number | "">("");
    const [productId, setProductId] = useState<string>("");
    const [qty, setQty] = useState<string>("1");

    // Data state
    const [sites, setSites] = useState<Site[]>([]);
    const [warehouses, setWarehouses] = useState<Warehouse[]>([]);

    // UI state
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingLists, setLoadingLists] = useState<boolean>(false);
    const [error, setError] = useState<string>("");

    const siteOptions = useMemo(() => toOptions(sites), [sites]);
    const whOptions = useMemo(() => toOptions(warehouses), [warehouses]);

    // Carrega sites (opcional) e um primeiro conjunto de warehouses
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                setLoadingLists(true);
                const [sitesList, whList] = await Promise.all([
                    fetchList<Site>("/core/sites/?ordering=code"),
                    fetchList<Warehouse>("/core/warehouses/?ordering=code"),
                ]);
                if (!mounted) return;
                setSites(sitesList);
                setWarehouses(whList);
                // Se houver apenas um warehouse, pré-seleciona
                if (!warehouseId && whList.length === 1) setWarehouseId(whList[0].id);
            } catch (e: any) {
                setError(e?.response?.data?.detail || "Falha ao carregar listas (sites/warehouses)");
            } finally {
                setLoadingLists(false);
            }
        })();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Recarrega warehouses quando o site muda
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                if (!siteId) return; // site é opcional
                setLoadingLists(true);
                let list: Warehouse[] = [];
                try {
                    list = await fetchList<Warehouse>(`/core/warehouses/?site=${siteId}&ordering=code`);
                } catch {
                    list = await fetchList<Warehouse>(`/core/warehouses/?site_id=${siteId}&ordering=code`);
                }
                if (!mounted) return;
                setWarehouses(list);
                setWarehouseId(list[0]?.id ?? "");
            } catch (e: any) {
                setError(e?.response?.data?.detail || "Falha ao filtrar warehouses pelo site");
            } finally {
                setLoadingLists(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, [siteId]);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setError("");

        const wid = Number(warehouseId);
        const pid = Number(productId);
        const q = Number(qty);

        if (!wid) return setError("Selecione um Warehouse.");
        if (!pid) return setError("Informe o ID do produto final.");
        if (!q || q <= 0) return setError("Quantidade deve ser maior que zero.");

        setLoading(true);
        try {
            const payload: any = { warehouse: wid, result_product: pid, qty: q };
            if (siteId) payload.site = Number(siteId);

            const { data } = await api.post("/manufacturing/copack-orders/", payload);

            const code = data?.code ? ` (código ${data.code})` : "";
            alert(`Copack Order criada com sucesso${code}.`);

            if (data?.id) {
                nav(`/manufacturing/copack-orders/${data.id}`);
            } else {
                nav(`/manufacturing/copack-orders`);
            }
        } catch (e: any) {
            const msg =
                e?.response?.data?.detail ||
                e?.response?.data?.non_field_errors?.[0] ||
                "Falha ao criar a Copack Order";
            setError(msg);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="app-container">
            <h1 className="text-2xl font-semibold mb-3">Nova Copack Order</h1>

            <p className="mb-6 text-sm text-neutral-600 dark:text-neutral-300">
                O código da ordem é gerado automaticamente (ex.: <b>CPK-001/2025</b>).
            </p>

            {/* Painel principal — mesmo contraste das outras telas */}
            <form
                onSubmit={handleSubmit}
                className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-5 md:p-6 space-y-5"
            >
                {/* SITE (opcional) */}
                <div>
                    <label className="block text-sm mb-1 text-neutral-800 dark:text-neutral-100">
                        Site (opcional)
                    </label>
                    <select
                        className="w-full rounded-xl px-3 py-2
                       bg-white text-neutral-900 border border-neutral-300
                       focus:outline-none focus:ring-2 ring-blue-500
                       dark:bg-neutral-900 dark:text-neutral-100 dark:border-neutral-700
                       placeholder-neutral-400"
                        value={siteId}
                        onChange={(e) => setSiteId(e.target.value ? Number(e.target.value) : "")}
                        disabled={loadingLists}
                    >
                        <option value="">— Sem filtro de site —</option>
                        {siteOptions.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                </div>

                {/* WAREHOUSE (obrigatório) */}
                <div>
                    <label className="block text-sm mb-1 text-neutral-800 dark:text-neutral-100">
                        Warehouse *
                    </label>
                    <select
                        className="w-full rounded-xl px-3 py-2
                       bg-white text-neutral-900 border border-neutral-300
                       focus:outline-none focus:ring-2 ring-blue-500
                       dark:bg-neutral-900 dark:text-neutral-100 dark:border-neutral-700
                       placeholder-neutral-400"
                        value={warehouseId}
                        onChange={(e) => setWarehouseId(Number(e.target.value))}
                        required
                        disabled={loadingLists}
                    >
                        <option value="">Selecione…</option>
                        {whOptions.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                </div>

                {/* PRODUTO FINAL (id) */}
                <div>
                    <label className="block text-sm mb-1 text-neutral-800 dark:text-neutral-100">
                        ID do produto final *
                    </label>
                    <input
                        type="number"
                        inputMode="numeric"
                        min={1}
                        className="w-full rounded-xl px-3 py-2
                       bg-white text-neutral-900 border border-neutral-300
                       focus:outline-none focus:ring-2 ring-blue-500
                       dark:bg-neutral-900 dark:text-neutral-100 dark:border-neutral-700
                       placeholder-neutral-400"
                        placeholder="ex.: 1"
                        value={productId}
                        onChange={(e) => setProductId(e.target.value)}
                        required
                    />
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                        (melhoraremos para autocomplete de produto em breve)
                    </p>
                </div>

                {/* QUANTIDADE */}
                <div>
                    <label className="block text-sm mb-1 text-neutral-800 dark:text-neutral-100">
                        Quantidade *
                    </label>
                    <input
                        type="number"
                        inputMode="decimal"
                        min={1}
                        step={1}
                        className="w-full rounded-xl px-3 py-2
                       bg-white text-neutral-900 border border-neutral-300
                       focus:outline-none focus:ring-2 ring-blue-500
                       dark:bg-neutral-900 dark:text-neutral-100 dark:border-neutral-700
                       placeholder-neutral-400"
                        value={qty}
                        onChange={(e) => setQty(e.target.value)}
                        required
                    />
                </div>

                {error && (
                    <div className="rounded-xl border border-red-600 bg-red-50 text-red-700 px-3 py-2
                          dark:bg-red-950/50 dark:text-red-200">
                        {error}
                    </div>
                )}

                <div className="flex gap-3 pt-1">
                    <button
                        type="submit"
                        disabled={loading}
                        className="px-4 py-2 rounded-xl font-medium
                       bg-blue-600 text-white hover:bg-blue-500
                       disabled:opacity-60"
                    >
                        {loading ? "Criando…" : "Criar"}
                    </button>
                    <button
                        type="button"
                        onClick={() => nav(-1)}
                        className="px-4 py-2 rounded-xl font-medium
                       bg-neutral-200 text-neutral-900 hover:bg-neutral-300
                       dark:bg-neutral-700 dark:text-neutral-100 dark:hover:bg-neutral-600"
                    >
                        Cancelar
                    </button>
                </div>
            </form>
        </div>
    );
}
